import { FC, useEffect, useRef, useState } from "react";
import {
  MuiBox,
  MuiGrid,
  MuiIconButton,
  MuiTable,
  WarningConfirmationModal,
} from "../../../../../../components";
import { createProgramSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import {
  CompatibleProgramTemplateResultFormKey,
  CompatibleResult,
} from "../../../../modelsAndSchemas/Models";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { ReactComponent as DeleteIcon } from "../../../../../../assets/images/delete-icon.svg";
import { useDeleteCompatibleTemplateMutation } from "../../../../../../api/services/programTemplate/programTemplateApis";
import { useDispatch } from "react-redux";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../redux/slices/appState/appStateSlice";
import { usePermissions } from "../../../../../../hooks/usePermissions";
import { MODULE_PERMISSIONS } from "../../../../../../helpers/constants";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";

interface CompatibleTemplateProps {
  pageId: string;
  rows: CompatibleResult[];
  templateId: string;
  refetchTemplateList?: () => void;
  isViewTemplate?: boolean;
}

export const CompatibleTemplateList: FC<CompatibleTemplateProps> = ({
  pageId,
  rows,
  templateId,
  refetchTemplateList,
  isViewTemplate = false,
}) => {
  const dispatch = useDispatch();
  const { fetchLabel, commonLabels } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const tableRef = useRef<{ resetPagination: () => void }>(null);
  const [templateList, setTemplateList] = useState([...rows]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedDeleteTemplateId, setSelectedDeleteTemplateId] = useState("");
  const { hasModulePermission } = usePermissions();

  const {
    mutateAsync: deleteCompatibleProgramTemplate,
    isSuccess: isDeleteSuccess,
    isPending: isDeletePending,
    isError: isDeleteError,
  } = useDeleteCompatibleTemplateMutation();

  const getLabel = (key: string) => {
    return fetchLabel(createProgramSiteCoreKeys.compatibleTemplateKey, key);
  };

  const handleDeleteConfirmClick = () => {
    deleteCompatibleProgramTemplate({
      programTemplateId: templateId,
      compatibleProgramTemplateId: selectedDeleteTemplateId,
    });
  };

  const handleDeletedClick = (id: string) => {
    setSelectedDeleteTemplateId(id);
    setShowConfirmationModal(true);
  };

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isDeletePending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isDeletePending]);

  useEffect(() => {
    if (isDeleteError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isDeleteError]);

  useEffect(() => {
    if (isDeleteSuccess) {
      refetchTemplateList?.();
      setShowConfirmationModal(false);
      dispatch(
        toggleToast({
          open: true,
          message: getLabel(
            createProgramSiteCoreKeys.compatibleTemplateDeletedMessage,
          ),
        }),
      );
    }
  }, [isDeleteSuccess]);

  const isEditTemplateColumn = [
    {
      id: "action",
      label: getLabel(createProgramSiteCoreKeys.compatibleAction),
      className: "action",
      render: (row: any) => {
        return (
          <MuiIconButton
            onClick={() => handleDeletedClick(row.id)}
            data-testid="delete-btn"
            aria-label={commonARIALabel.delete}
          >
            <DeleteIcon />
          </MuiIconButton>
        );
      },
    },
  ];

  const columns = [
    {
      id: CompatibleProgramTemplateResultFormKey.countryName,
      label: getLabel(createProgramSiteCoreKeys.compatibleCountry),
      isSortVisible: true,
      className: "country-name",
    },
    {
      id: CompatibleProgramTemplateResultFormKey.compatibleProgramTemplateName,
      label: getLabel(createProgramSiteCoreKeys.compatibleProgramTemplate),
      isSortVisible: true,
    },
    {
      id: CompatibleProgramTemplateResultFormKey.duration,
      label: getLabel(createProgramSiteCoreKeys.duration),
      isSortVisible: true,
    },
    ...(isViewTemplate ||
    !hasModulePermission(MODULE_PERMISSIONS.DELETE_COMPATIBLE_TEMPLATES)
      ? []
      : isEditTemplateColumn),
  ];

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  useEffect(() => {
    resetPagination();
    setTemplateList([...rows]);
  }, [rows]);

  return (
    <MuiBox className="table-container">
      <MuiGrid container>
        <MuiGrid size={{ xs: 12 }}>
          <MuiTable
            ref={tableRef}
            pageId={pageId}
            tableHeaderForm={[]}
            columns={columns}
            tableResults={[...templateList]}
            noDataInApi={templateList.length === 0}
            noResultMessage={getLabel(
              createProgramSiteCoreKeys.notAvailableMessageCompatible,
            )}
            emptyMessage={getLabel(
              createProgramSiteCoreKeys.notAvailableMessageCompatible,
            )}
            priorityResults={[]}
          />
        </MuiGrid>
      </MuiGrid>
      {showConfirmationModal && (
        <WarningConfirmationModal
          show={showConfirmationModal}
          modalTitle={commonLabels.confirmation}
          onClose={() => setShowConfirmationModal(false)}
          onPrimaryButtonClick={handleDeleteConfirmClick}
          labelContent={getLabel(
            createProgramSiteCoreKeys.deleteCompatibleTemplateConirmationMessage,
          )}
          secondaryButtonTitle={commonLabels.no}
        />
      )}
    </MuiBox>
  );
};
